import { Undefinedable, cclFetch, createInternalAppError } from 'gocclr-shared-ui/dist/helpers'
import DOMPurify from 'dompurify';
import { getHeaderUrl, getUser } from '../app-context/accounts-context';
import { NavigateFunction } from 'react-router';
import { PAGES } from '../model/constants';
import { getAppState } from '../hooks/app-state';

interface GoCCLPortalUIAppUtils {
    getViewport(): void;
    setDisplayType(): void;
    promotionalAlert(): void;
    sanitizeURL(url?: string): string;
}

interface GoCCLPortalUIAppModule {
    init(): void;
}

interface GoCCLPortalUIApp {
    utils?: GoCCLPortalUIAppUtils;
    footer?: GoCCLPortalUIAppModule;
    header?: GoCCLPortalUIAppModule;
    alert_messaging?: GoCCLPortalUIAppModule;
}

interface GoCCLPortalWindow extends Window {
    APP?: GoCCLPortalUIApp;
}

const parser = new DOMParser();
let currentPromise: Undefinedable<Promise<string>> = undefined;

function getRawHeader(headerUrl: string): Promise<string> {
    if (currentPromise) {
        return currentPromise;
    }

    currentPromise = cclFetch<string>(headerUrl, { credentials: 'include' }).then((response) => {
        return DOMPurify.sanitize(response);
    });
    return currentPromise;
}

export async function getHeaderHtml(): Promise<string> {
    const rawHeader = getAppState('rawHeader');
    if (rawHeader) {
        return Promise.resolve(rawHeader);
    }

    const user = getUser();
    const headerUrl = getHeaderUrl(!!user);
    const rawHtml = await getRawHeader(headerUrl);

    const parsedDoc = parser.parseFromString(rawHtml, 'text/html');
    const errorNode = parsedDoc.querySelector('parsererror');
    if (errorNode) {
        throw createInternalAppError('Could not parse the header response');
    }
    const headerWrap = parsedDoc.querySelector('#ccl-headerWrap');
    const headerHtml = headerWrap?.outerHTML || '';
    currentPromise = undefined;

    return headerHtml;
}

export async function getGlobalAlertsHtml(): Promise<string> {
    const rawHeader = getAppState('rawAlerts');
    if (rawHeader) {
        return Promise.resolve(rawHeader);
    }

    const user = getUser();
    const headerUrl = getHeaderUrl(!!user);
    const rawHtml = await getRawHeader(headerUrl);

    const parsedDoc = parser.parseFromString(rawHtml, 'text/html');
    const errorNode = parsedDoc.querySelector('parsererror');
    if (errorNode) {
        currentPromise = undefined;
        throw createInternalAppError('Could not parse the header response');
    }

    const alerts = parsedDoc.querySelectorAll('.ccl-alert');
    let aggregateAlerts = '';

    for (let i = 0; i < alerts.length; i++) {
        const alert = alerts.item(i);
        aggregateAlerts += alert.outerHTML;
    }

    if (!aggregateAlerts) {
        aggregateAlerts = '<div></div>';
    }

    currentPromise = undefined;

    return aggregateAlerts;
}

export function initHeader(navigate: NavigateFunction) {
    const interval = setInterval(() => {
        if (!document.getElementById('ccl-headerWrap') || !document.getElementById('ccl-headerWrap')) {
            return;
        }

        clearInterval(interval);

        const portalWindow: GoCCLPortalWindow = window;
        portalWindow.APP?.utils?.getViewport();
        portalWindow.APP?.utils?.setDisplayType();
        portalWindow.APP?.header?.init();

        document.querySelector<HTMLAnchorElement>('.ccl-logout')?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            document.querySelector<HTMLFormElement>('#accounts-log-out')?.submit();
            return false;
        });


        const viewProfileElement = document.querySelector<HTMLAnchorElement>('.ccl-user-info a.ccl-view-profile');
        if (viewProfileElement) {
            viewProfileElement?.addEventListener('click', (event) => {
                event.preventDefault();
                navigate(PAGES.MY_PROFILE);
                return false;
            }, false);
        }

    }, 100);
}

export function initAlerts() {
    const interval = setInterval(() => {
        if (!document.getElementById('ccl-headerWrap') || !document.getElementById('ccl-headerWrap')) {
            return;
        }

        clearInterval(interval);

        const portalWindow: GoCCLPortalWindow = window;
        portalWindow.APP?.utils?.getViewport();
        portalWindow.APP?.utils?.setDisplayType();
        portalWindow.APP?.utils?.promotionalAlert();
    }, 100);
}

export function clearHeaderPromise() {
    currentPromise = undefined;
}